import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Label } from '../ui/label.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import { Input } from '../ui/input.tsx';
import type { AssignedProperty } from '@metron/shared';

export interface PropertyMultiSelectProps {
  availableProperties: AssignedProperty[];
  loadingProperties: boolean;
  selectedPropertyIds: number[];
  onPropertyToggle: (propertyId: number, checked: boolean) => void;
  disabled?: boolean;
}

/**
 * Searchable multi-select for a customer's properties. Mirrors the
 * CustomerPicker look/feel (search input + filtered list) but allows selecting
 * multiple properties via checkboxes.
 */
export function PropertyMultiSelect(props: PropertyMultiSelectProps) {
  const {
    availableProperties,
    loadingProperties,
    selectedPropertyIds,
    onPropertyToggle,
    disabled,
  } = props;

  const [searchQuery, setSearchQuery] = useState('');
  const trimmed = searchQuery.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!trimmed) return availableProperties;
    return availableProperties.filter((p) =>
      p.name.toLowerCase().includes(trimmed) ||
      p.metronId.toLowerCase().includes(trimmed) ||
      p.id.toString().includes(trimmed)
    );
  }, [availableProperties, trimmed]);

  return (
    <div>
      <Label className='text-sm font-medium mb-2 block'>
        Properties
        {selectedPropertyIds.length > 0 && (
          <span className='ml-2 text-xs font-normal text-muted-foreground'>
            ({selectedPropertyIds.length} selected)
          </span>
        )}
      </Label>

      {loadingProperties && (
        <div className='flex items-center justify-center py-4'>
          <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-primary'></div>
        </div>
      )}

      {!loadingProperties && availableProperties.length === 0 && (
        <p className='text-sm text-muted-foreground'>No properties found for this customer.</p>
      )}

      {!loadingProperties && availableProperties.length > 0 && (
        <>
          <div className='relative'>
            <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search properties…'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='pl-8'
              disabled={disabled}
            />
          </div>

          <div className='mt-2 border rounded-md max-h-60 overflow-y-auto'>
            {filtered.length === 0
              ? <p className='p-3 text-sm text-muted-foreground'>No properties match.</p>
              : (
                filtered.map((property) => (
                  <label
                    key={property.id}
                    className='flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-accent transition-colors border-b last:border-b-0'
                  >
                    <Checkbox
                      checked={selectedPropertyIds.includes(property.id)}
                      onCheckedChange={(checked) => onPropertyToggle(property.id, !!checked)}
                      disabled={disabled}
                      id={`property-${property.id}`}
                    />
                    <div>
                      <div className='text-sm font-medium'>{property.name}</div>
                      <div className='text-xs text-muted-foreground'>{property.metronId}</div>
                    </div>
                  </label>
                ))
              )}
          </div>

          {selectedPropertyIds.length === 0 && (
            <p className='text-sm text-muted-foreground mt-1'>Select at least one property.</p>
          )}
        </>
      )}
    </div>
  );
}
